import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams, useLocation } from '@tanstack/react-router'
import {
  Plus,
  Trash2,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Eye,
  EyeOff,
  ArrowUp,
  ArrowDown,
  HelpCircle,
} from 'lucide-react'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import { RichEditor } from '../components/RichEditor'
import { apiGet, apiPost, apiPut, apiDelete } from '../lib/api'

type Faq = {
  id: number
  categoria: string
  pregunta: string
  respuesta: string
  orden: number
  activo: boolean
}

type Draft = {
  categoria: string
  pregunta: string
  respuesta: string
  orden: number
  activo: boolean
}

const EMPTY: Draft = {
  categoria: 'general',
  pregunta: '',
  respuesta: '',
  orden: 0,
  activo: true,
}

export function FAQ() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams({ strict: false }) as { id?: string }
  const [flash, setFlash] = useState<{ tipo: 'ok' | 'err'; msg: string } | null>(null)
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [editingId, setEditingId] = useState<number | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['faq'],
    queryFn: () => apiGet<{ data: Faq[] }>('/api/admin/faq'),
  })

  const items = data?.data ?? []

  // Sync state to URL: /faq/nuevo -> create, /faq/:id -> highlight, /faq -> list
  useEffect(() => {
    if (location.pathname === '/faq/nuevo') {
      setAdding(true)
      setEditingId(null)
    } else if (params.id) {
      const idNum = Number(params.id)
      setEditingId(Number.isFinite(idNum) ? idNum : null)
      setAdding(false)
    } else {
      setEditingId(null)
    }
  }, [location.pathname, params.id])

  function notify(tipo: 'ok' | 'err', msg: string) {
    setFlash({ tipo, msg })
    setTimeout(() => setFlash(null), 3000)
  }

  function refresh() {
    qc.invalidateQueries({ queryKey: ['faq'] })
  }

  const createMut = useMutation({
    mutationFn: (body: Draft) => apiPost<{ id: number }>('/api/admin/faq', body),
    onSuccess: () => {
      notify('ok', 'FAQ creada')
      setAdding(false)
      setDraft(EMPTY)
      refresh()
      navigate({ to: '/faq' })
    },
    onError: (e: Error) => notify('err', e.message),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, ...body }: { id: number } & Record<string, unknown>) =>
      apiPut<{ ok: true }>(`/api/admin/faq/${id}`, body),
    onSuccess: () => refresh(),
    onError: (e: Error) => notify('err', e.message),
  })

  const deleteMut = useMutation({
    mutationFn: (id: number) => apiDelete<{ ok: true }>(`/api/admin/faq/${id}`),
    onSuccess: () => {
      notify('ok', 'FAQ eliminada')
      refresh()
    },
    onError: (e: Error) => notify('err', e.message),
  })

  function confirmAdd() {
    if (!draft.pregunta.trim() || !draft.respuesta.trim()) {
      notify('err', 'Falta pregunta o respuesta')
      return
    }
    const nextOrder = (items.at(-1)?.orden ?? 0) + 1
    createMut.mutate({
      ...draft,
      categoria: draft.categoria.trim() || 'general',
      orden: nextOrder,
    })
  }

  function move(item: Faq, dir: -1 | 1) {
    const idx = items.findIndex((i) => i.id === item.id)
    const swap = items[idx + dir]
    if (!swap) return
    updateMut.mutate({ id: item.id, orden: swap.orden })
    updateMut.mutate({ id: swap.id, orden: item.orden })
  }

  return (
    <>
      <Topbar
        title="FAQ"
        subtitle="Preguntas frecuentes mostradas en /faq y usadas para el schema FAQPage (SEO)"
        actions={
          <>
            {data && <Badge variant="info">{items.length} FAQs</Badge>}
            <button
              type="button"
              onClick={() => navigate({ to: '/faq/nuevo' })}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700"
            >
              <Plus className="size-3.5" />
              Nueva pregunta
            </button>
          </>
        }
      />

      {flash && (
        <div
          className={`mx-6 mt-4 flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
            flash.tipo === 'ok'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : 'border-red-200 bg-red-50 text-red-800'
          }`}
        >
          {flash.tipo === 'ok' ? (
            <CheckCircle2 className="size-4" />
          ) : (
            <AlertCircle className="size-4" />
          )}
          <span>{flash.msg}</span>
        </div>
      )}

      <div className="p-6">
        {isLoading ? (
          <div className="flex items-center gap-2 text-ink-500">
            <Loader2 className="size-4 animate-spin" /> Cargando FAQs…
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((item, idx) => (
              <div
                key={item.id}
                className={`rounded-2xl border bg-white p-4 shadow-sm ${
                  editingId === item.id
                    ? 'border-brand-500 ring-2 ring-brand-200'
                    : 'border-ink-200'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="grid size-10 shrink-0 place-items-center rounded-lg bg-brand-50 text-brand-600">
                    <HelpCircle className="size-5" />
                  </div>

                  <div className="grid flex-1 gap-2">
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-12">
                      <input
                        type="text"
                        value={item.categoria}
                        placeholder="Categoría"
                        onChange={(e) =>
                          updateMut.mutate({ id: item.id, categoria: e.target.value })
                        }
                        className="sm:col-span-3 rounded-md border border-ink-200 bg-white px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-ink-700 outline-none focus:border-brand-500"
                      />
                      <input
                        type="text"
                        value={item.pregunta}
                        placeholder="Pregunta"
                        onChange={(e) =>
                          updateMut.mutate({ id: item.id, pregunta: e.target.value })
                        }
                        className="sm:col-span-9 rounded-md border border-ink-200 bg-white px-2 py-1.5 text-sm font-semibold outline-none focus:border-brand-500"
                      />
                    </div>
                    <RichEditor
                      value={item.respuesta}
                      onChange={(v) => updateMut.mutate({ id: item.id, respuesta: v })}
                      placeholder="Respuesta"
                      minHeight={140}
                    />
                  </div>

                  <div className="flex flex-col items-center gap-1">
                    <button
                      type="button"
                      title={item.activo ? 'Desactivar' : 'Activar'}
                      onClick={() =>
                        updateMut.mutate({ id: item.id, activo: !item.activo })
                      }
                      className="grid size-8 place-items-center rounded text-ink-500 hover:bg-ink-100"
                    >
                      {item.activo ? (
                        <Eye className="size-4 text-emerald-600" />
                      ) : (
                        <EyeOff className="size-4 text-ink-400" />
                      )}
                    </button>
                    <button
                      type="button"
                      disabled={idx === 0}
                      onClick={() => move(item, -1)}
                      className="grid size-8 place-items-center rounded text-ink-500 hover:bg-ink-100 disabled:opacity-30"
                    >
                      <ArrowUp className="size-4" />
                    </button>
                    <button
                      type="button"
                      disabled={idx === items.length - 1}
                      onClick={() => move(item, 1)}
                      className="grid size-8 place-items-center rounded text-ink-500 hover:bg-ink-100 disabled:opacity-30"
                    >
                      <ArrowDown className="size-4" />
                    </button>
                    <button
                      type="button"
                      title="Eliminar"
                      onClick={() => {
                        if (confirm(`Eliminar "${item.pregunta}"?`)) deleteMut.mutate(item.id)
                      }}
                      className="grid size-8 place-items-center rounded text-red-500 hover:bg-red-50"
                    >
                      <Trash2 className="size-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {adding && (
              <div className="rounded-2xl border border-dashed border-brand-300 bg-brand-50 p-4">
                <div className="grid gap-2 sm:grid-cols-12">
                  <input
                    type="text"
                    value={draft.categoria}
                    placeholder="Categoría"
                    onChange={(e) => setDraft((d) => ({ ...d, categoria: e.target.value }))}
                    className="sm:col-span-3 rounded-md border border-ink-200 bg-white px-2 py-1.5 text-xs font-semibold uppercase tracking-wide outline-none focus:border-brand-500"
                  />
                  <input
                    type="text"
                    autoFocus
                    value={draft.pregunta}
                    placeholder="Pregunta"
                    onChange={(e) => setDraft((d) => ({ ...d, pregunta: e.target.value }))}
                    className="sm:col-span-9 rounded-md border border-ink-200 bg-white px-2 py-1.5 text-sm font-semibold outline-none focus:border-brand-500"
                  />
                  <div className="sm:col-span-12">
                    <RichEditor
                      value={draft.respuesta}
                      onChange={(v) => setDraft((d) => ({ ...d, respuesta: v }))}
                      placeholder="Respuesta"
                      minHeight={160}
                    />
                  </div>
                </div>
                <div className="mt-3 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setAdding(false)
                      setDraft(EMPTY)
                      navigate({ to: '/faq' })
                    }}
                    className="rounded-md border border-ink-200 bg-white px-3 py-1.5 text-xs text-ink-600 hover:bg-ink-100"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={confirmAdd}
                    className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-700"
                  >
                    Crear FAQ
                  </button>
                </div>
              </div>
            )}

            {!isLoading && items.length === 0 && !adding && (
              <div className="rounded-2xl border border-dashed border-ink-200 bg-white p-10 text-center">
                <h3 className="text-sm font-semibold text-ink-900">Sin FAQs</h3>
                <p className="mt-1 text-xs text-ink-500">
                  Crea la primera pregunta con el botón de arriba.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}
